import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../common/redux/reduxHooks";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import {
    CreateDisponibiliteStructure,
    UserStructureResponse,
} from "../structures/model/StructureModel";
import { createProject } from "../structures/redux/projectAction";
import { language, LanguageType } from "../utils/LanguageUtils";
import {
    Button,
    Grid,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    styled,
} from "@mui/material";
import "moment/locale/fr";
import { Modal } from "../common/components/Modal";
import StyledDatePicker from "../common/components/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import CircularProgress from "@mui/material/CircularProgress";
import TaskAltSharpIcon from "@mui/icons-material/TaskAltSharp";
import { useTranslation } from "react-i18next";
import CountrySelect from "../common/components/CountryInput";
import { AdministrativeArea, Country } from "../common/redux/countryActions";
import CityInput from "../common/components/CityInput";
import AdmnistrativeAreaSelect from "../common/components/AdmnistrativeAreaSelect";
import { RouteAPI } from "../common/service/apiService";
import SubscriptionModal, {
    SubscriptionModalHandle,
} from "../common/components/SubscriptionModal";
import { getUser } from "../common/redux/userAction";
import { RootState } from "../common/redux/store";
import { Profession } from "../common/redux/professionAction";
import ProfessionSelect from "../common/components/ProfessionInput";

const initialValueProject: CreateDisponibiliteStructure = {
    country: null,
    administrativeArea: null,
    ville: null,
    profession: "",
    selection: [""],
    description: "",
    type: "",
    dateDemarrage: "",
    dateFin: "",
    heureDemarrage: "",
    heureFin: "",
};

export const StructureForm = () => {
    const [dayPeriod, setDayPeriod] = useState("");
    const [weekPeriod, setWeekPeriod] = useState("");
    const [weekOpen, setWeekOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [titleModal, setTitleModal] = useState<string>("");
    const [dayOpen, setDayOpen] = useState(false);
    const [messageModal, setMessageModal] = useState<string>("");
    const [projectData, setProjectData] =
        useState<CreateDisponibiliteStructure>(initialValueProject);
    const [startDateValue, setStartDateValue] = useState<dayjs.Dayjs | null>(
        null
    );
    const [endDateValue, setEndDateValue] = useState<dayjs.Dayjs | null>(null);
    const [heureDemarrage, setHeureDemarrage] = useState<dayjs.Dayjs | null>(
        null
    );
    const [heureFin, setHeureFin] = useState<dayjs.Dayjs | null>(null);
    const [isDisplayPopUpCreateProject, setIsDisplayPopUpCreateProject] =
        useState(false);
    const [profession, setProfession] = useState<Profession[]>([]);
    const [errorMessage, setErrorMessage] = useState<{
        description?: string;
        profession?: string;
        country?: string;
        administrativeArea?: string;
        period?: string;
        startDate?: string;
        endDate?: string;
        city?: string;
    }>({});
    const [country, setCountry] = useState<Country | null>(null);
    const [administrativeArea, setAdministrativeArea] =
        useState<AdministrativeArea | null>(null);

    const modalRef = useRef<SubscriptionModalHandle>(null);

    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const lang: LanguageType = language();
    const userSelector: UserStructureResponse = useAppSelector(
        (state: RootState) => state.user.data
    );

    const handleDayPeriod = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null
    ) => {
        if (newAlignment !== null) {
            setDayPeriod(newAlignment);
        }
    };

    const handleWeekPeriod = (
        event: React.MouseEvent<HTMLElement>,
        newAlignment: string | null
    ) => {
        if (newAlignment !== null) {
            setWeekPeriod(newAlignment);
        }
    };

    const changeDatePicker = (e: dayjs.Dayjs | null, type: string) => {
        if (e) {
            const dateFormatted = e.format("YYYY-MM-DD");
            if (type === "startdate") {
                setProjectData({
                    ...projectData,
                    dateDemarrage: dateFormatted,
                });
                setStartDateValue(e);
            } else if (type === "enddate") {
                setEndDateValue(e);
                setProjectData({
                    ...projectData,
                    dateFin: dateFormatted,
                });
            }
        }
    };

    const handleHoursChange = (value: dayjs.Dayjs | null, type: string) => {
        if (value) {
            const timeFormatted = value.format("HH:mm");
            if (type === "heureDemarrage") {
                setHeureDemarrage(value);
                setProjectData({
                    ...projectData,
                    heureDemarrage: timeFormatted,
                });
            } else if (type === "heureFin") {
                setHeureFin(value);
                setProjectData({
                    ...projectData,
                    heureFin: timeFormatted,
                });
            }
        }
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (isSubmitting) return;

        const errors: {
            description?: string;
            profession?: string;
            country?: string;
            administrativeArea?: string;
            city?: string;
            period?: string;
            startDate?: string;
            endDate?: string;
        } = {};

        if (!projectData.description || projectData.description.trim() === "") {
            errors.description = t("projectForm.validation.description");
        }

        if (
            !projectData.profession ||
            projectData.profession.length < 1 ||
            !projectData.profession[0]
        ) {
            errors.profession = t("projectForm.validation.profession");
        }

        if (!country?.id) {
            errors.country = t("projectForm.validation.country");
        }

        if (!administrativeArea?.id) {
            errors.administrativeArea = t(
                "projectForm.validation.administrativeArea"
            );
        }

        if (!projectData.ville || projectData.ville.trim() === "") {
            errors.city = t("projectForm.validation.city");
        }

        if (!dayPeriod || !weekPeriod) {
            errors.period = t("projectForm.validation.period");
        }

        if (!startDateValue || !heureDemarrage) {
            errors.startDate = t("projectForm.validation.startDate");
        }

        if (!endDateValue || !heureFin) {
            errors.endDate = t("projectForm.validation.endDate");
        }

        setErrorMessage(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        setIsSubmitting(true);

        if (Object.keys(errors).length === 0) {
            dispatch(
                createProject({
                    ...projectData,
                    selection: [dayPeriod, weekPeriod],
                    dateDemarrage: startDateValue
                        ? startDateValue.format("YYYY-MM-DD")
                        : null,
                    dateFin: endDateValue
                        ? endDateValue.format("YYYY-MM-DD")
                        : null,
                    heureDemarrage: heureDemarrage?.format("HH:mm:ss") ?? null,
                    heureFin: heureFin?.format("HH:mm:ss") ?? null,
                    country: `${RouteAPI.COUNTRY}/${country!.id}`,
                    administrativeArea: `${RouteAPI.ADMINISTRATIVE_AREA}/${
                        administrativeArea!.id
                    }`,
                    profession: profession[0]?.id
                        ? `/api/professions/${profession[0].id}`
                        : "",
                })
            )
                .then((res: any) => {
                    console.log("res", res);
                    if (res.error) {
                        setErrorMessage({
                            profession:
                                res.payload.error?.profession ||
                                t("error.unknown"),
                        });

                        modalRef.current?.open();

                        return;
                    }

                    setProjectData({
                        ...initialValueProject,
                        ville: null,
                    });
                    setDayPeriod("");
                    setWeekPeriod("");
                    setStartDateValue(null);
                    setEndDateValue(null);
                    setHeureDemarrage(null);
                    setHeureFin(null);

                    setTitleModal(lang.congratulation);
                    setMessageModal(lang.createProjectValidation);
                    setIsDisplayPopUpCreateProject(true);
                    setCountry(null);
                    setAdministrativeArea(null);
                    dispatch(getUser(userSelector?.id));
                })
                .catch()
                .finally(() => {
                    setIsSubmitting(false);
                });
        }
    };

    useEffect(() => {
        if (
            projectData.description?.trim() !== "" &&
            errorMessage.description
        ) {
            setErrorMessage((prev) => ({
                ...prev,
                description: undefined,
            }));
        }
    }, [errorMessage.description, projectData.description]);

    return (
        <>
            <ProjectContainer>
                <Grid item sx={{ mt: 6 }}>
                    <Typography
                        sx={{
                            gridArea: "title",
                            fontSize: "3ex",
                            width: "max-content",
                            fontWeight: "bold",
                            color: "white",
                        }}
                    >
                        {lang.createProject}
                    </Typography>
                </Grid>

                <InputsSection>
                    <InputsContainer>
                        <Stack
                            sx={{
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "1rem",
                            }}
                        >
                            <ProfessionSelect
                                required
                                value={profession}
                                onChange={(newValue: Profession[]) => {
                                    setProfession(newValue);
                                    setProjectData({
                                        ...projectData,
                                        profession:
                                            newValue.length > 0
                                                ? `/api/professions/${newValue[0].id}`
                                                : "",
                                    });
                                }}
                                sx={{
                                    backgroundColor: "white",
                                    borderRadius: "5px",
                                    width: "335px",

                                    "@media(width >= 1200px)": {
                                        width: "415px",
                                    },

                                    "@media(width >= 1900px)": {
                                        width: "575px",
                                    },
                                }}
                            />
                            <Stack
                                sx={{
                                    minWidth: "max-content",
                                }}
                            >
                                <CountrySelect
                                    value={country}
                                    onChange={setCountry}
                                    id="country"
                                    name="country"
                                    errorMessage={errorMessage.country}
                                    sx={{
                                        width: "335px",
                                        "@media(width >= 1200px)": {
                                            width: "415px",
                                        },
                                        "@media(width >= 1900px)": {
                                            width: "475px",
                                        },
                                    }}
                                />
                            </Stack>
                            <Stack
                                sx={{
                                    minWidth: "max-content",
                                }}
                            >
                                <AdmnistrativeAreaSelect
                                    country={country}
                                    value={administrativeArea}
                                    errorMessage={
                                        errorMessage.administrativeArea
                                    }
                                    onChange={setAdministrativeArea}
                                    sx={{
                                        width: "335px",
                                        "@media(width >= 1200px)": {
                                            width: "415px",
                                        },
                                        "@media(width >= 1900px)": {
                                            width: "475px",
                                        },
                                    }}
                                />
                            </Stack>
                            <Stack
                                sx={{
                                    minWidth: "max-content",
                                }}
                            >
                                <CityInput
                                    countryCode={country?.code || ""}
                                    countryName={country?.name || ""}
                                    value={projectData.ville}
                                    errorMessage={errorMessage.city}
                                    label={t("projectForm.labels.city")}
                                    onChange={(newCity) => {
                                        setProjectData((prev) => ({
                                            ...prev,
                                            ville: newCity,
                                        }));
                                    }}
                                    sx={{
                                        width: "335px",
                                        "@media(width >= 1200px)": {
                                            width: "415px",
                                        },
                                        "@media(width >= 1900px)": {
                                            width: "475px",
                                        },
                                    }}
                                />
                            </Stack>
                        </Stack>
                        <Stack
                            sx={{
                                flexDirection: "column",
                                justifyContent: "center",
                                gap: "1rem",
                            }}
                        >
                            <Stack
                                sx={{
                                    minWidth: "max-content",
                                    flexDirection: "row",
                                    gap: "1rem",
                                }}
                                flexWrap={"nowrap"}
                            >
                                <StyledDatePicker
                                    open={dayOpen}
                                    value={startDateValue}
                                    onOpen={() => setDayOpen(true)}
                                    onClose={() => setDayOpen(false)}
                                    onChange={(newValue) =>
                                        changeDatePicker(
                                            newValue as dayjs.Dayjs | null,
                                            "startdate"
                                        )
                                    }
                                    label={t("project.date.start")}
                                />

                                <StyledDatePicker
                                    open={weekOpen}
                                    value={endDateValue}
                                    onOpen={() => setWeekOpen(true)}
                                    onClose={() => setWeekOpen(false)}
                                    onChange={(newValue) =>
                                        changeDatePicker(
                                            newValue as dayjs.Dayjs | null,
                                            "enddate"
                                        )
                                    }
                                    label={t("project.date.end")}
                                />
                            </Stack>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Stack direction="row" spacing={2}>
                                    <TimePicker
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "5px",
                                            width: "160px",
                                            height: "fit-content",
                                            color: "#3b3c36",
                                            "& fieldset": { border: "none" },
                                            "@media(width >= 1200px)": {
                                                width: "200px",
                                            },
                                            "@media(width >= 1900px)": {
                                                width: "280px",
                                            },
                                        }}
                                        ampm={false}
                                        label={t("project.hour.start")}
                                        value={heureDemarrage}
                                        onChange={(newValue) =>
                                            handleHoursChange(
                                                newValue,
                                                "heureDemarrage"
                                            )
                                        }
                                    />
                                    <TimePicker
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "5px",
                                            width: "160px",
                                            height: "fit-content",
                                            color: "#3b3c36",
                                            "& fieldset": { border: "none" },
                                            "@media(width >= 1200px)": {
                                                width: "200px",
                                            },
                                            "@media(width >= 1900px)": {
                                                width: "280px",
                                            },
                                        }}
                                        ampm={false}
                                        label={t("project.hour.end")}
                                        value={heureFin}
                                        onChange={(newValue) =>
                                            handleHoursChange(
                                                newValue,
                                                "heureFin"
                                            )
                                        }
                                    />
                                </Stack>
                                {(errorMessage.startDate ||
                                    errorMessage.endDate) && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{
                                            backgroundColor: "white",
                                            padding: "0.5rem",
                                            borderRadius: "5px",
                                        }}
                                    >
                                        {errorMessage.startDate}{" "}
                                        {errorMessage.endDate}
                                    </Typography>
                                )}
                            </LocalizationProvider>

                            <ButtonGroup
                                value={dayPeriod}
                                exclusive
                                id="Jour"
                                size="medium"
                                onChange={handleDayPeriod}
                                color="warning"
                                aria-label="text alignment"
                            >
                                <MyToggleButton
                                    value="jour"
                                    aria-label="left aligned"
                                >
                                    {t("select.day")}
                                </MyToggleButton>
                                <MyToggleButton
                                    value="nuit"
                                    aria-label="centered"
                                >
                                    {t("select.night")}
                                </MyToggleButton>
                                <MyToggleButton
                                    value="indifferent"
                                    aria-label="right aligned"
                                >
                                    {t("select.indifferent")}
                                </MyToggleButton>
                            </ButtonGroup>

                            <ButtonGroup
                                value={weekPeriod}
                                exclusive
                                id="Semaine"
                                size="medium"
                                onChange={handleWeekPeriod}
                                color="warning"
                                aria-label="text alignment"
                            >
                                <MyToggleButton
                                    value="semaines"
                                    aria-label="left aligned"
                                >
                                    {t("select.period.weekend")}
                                </MyToggleButton>
                                <MyToggleButton
                                    value="weekend"
                                    aria-label="centered"
                                >
                                    Week-End
                                </MyToggleButton>
                                <MyToggleButton
                                    value="indifferent"
                                    aria-label="right aligned"
                                >
                                    {t("select.indifferent")}
                                </MyToggleButton>
                            </ButtonGroup>
                            {errorMessage.period && (
                                <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{
                                        backgroundColor: "white",
                                        padding: "0.5rem",
                                        borderRadius: "5px",
                                    }}
                                >
                                    {errorMessage.period}
                                </Typography>
                            )}
                        </Stack>
                    </InputsContainer>
                    <TextareaSection>
                        <Textarea>
                            <TextField
                                name="description"
                                placeholder={t("project.description")}
                                value={projectData.description}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setProjectData({
                                        ...projectData,
                                        description: value,
                                    });
                                }}
                                minRows={2}
                                maxRows={3}
                                inputProps={{ maxLength: 150 }}
                                multiline
                                error={!!errorMessage.description}
                                helperText={errorMessage.description}
                            />
                        </Textarea>
                        <Submit
                            type="submit"
                            disabled={isSubmitting}
                            onClick={handleSubmit}
                        >
                            {isSubmitting ? (
                                <CircularProgress
                                    size={24}
                                    sx={{ color: "white" }}
                                />
                            ) : (
                                <TaskAltSharpIcon
                                    fontSize="small"
                                    sx={{ width: 58, height: 58 }}
                                />
                            )}
                        </Submit>
                        <Modal
                            open={isDisplayPopUpCreateProject}
                            title={titleModal}
                            message={messageModal}
                            showBackground={true}
                            arrayOfMessage={Object.values(errorMessage)}
                            credentials=" "
                            onClose={() =>
                                setIsDisplayPopUpCreateProject(false)
                            }
                            onValidation={() => {
                                setIsDisplayPopUpCreateProject(false);
                            }}
                            titleButtonValidation={t("button.modal.valid")}
                        ></Modal>
                    </TextareaSection>
                </InputsSection>
            </ProjectContainer>

            <SubscriptionModal ref={modalRef} />
        </>
    );
};

const ProjectContainer = styled(Stack)({
    display: "flex",
    flexDirection: "column",
    marginTop: "3rem",
    gap: "1rem",
    padding: "1rem",
    alignItems: "center",
    width: "100dvw",
    background: "#FF8A80",
});

const InputsSection = styled(Stack)({
    flexDirection: "column",
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: "1.5rem",
    marginTop: "1rem",

    "@media(width >= 990px)": {
        flexDirection: "row",
    },
});

const InputsContainer = styled(Stack)({
    flexDirection: "column",
    width: "100%",
    gap: "1.5rem",
    justifyContent: "center",
    alignItems: "center",

    "@media(width >= 990px)": {
        flexDirection: "row",
        width: "60%",
    },

    "@media(width >= 1900px)": {
        width: "50%",
    },
});

const MyToggleButton = styled(ToggleButton)({
    background: "white",
    boxShadow:
        "rgba(0, 0, 0, 0.2) 0px 2px 2px, rgba(0, 0, 0, 0.3) 0px 3px 3px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
    borderRadius: "5px",
    fontSize: "13px",
    width: "113px",

    "&.Mui-selected, &.Mui-selected:hover": {
        color: "#36454F",
        backgroundColor: "#FF8A80",
    },
    "&:hover": { backgroundColor: "#E5E4E2" },

    "@media (width >= 765px)": {
        width: "115px",
    },

    "@media(width >= 1200px)": {
        width: "140px",
    },
});

const ButtonGroup = styled(ToggleButtonGroup)({
    display: "flex",
    flexDirection: "row",
    borderRadius: "10px",
    height: "55px",
    width: "335px",
    boxShadow:
        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",

    "@media(width >= 768px)": {
        width: "335px",
        height: "45px",
    },

    "@media(width >= 1200px)": {
        width: "415px",
    },
});

const Submit = styled(Button)({
    borderRadius: "50%",
    width: "50px",
    height: "65px",
    padding: "0.5rem",
    alignSelf: "center",
    color: "white",
    boxShadow:
        "rgba(0, 0, 0, 0.4) 0px 2px 4px, rgba(0, 0, 0, 0.3) 0px 7px 13px -3px, rgba(0, 0, 0, 0.2) 0px -3px 0px inset",
    background:
        "linear-gradient(to bottom, #fdaaaa, #fda2a2, #fc9999, #fc9191, #fb8888, #f98282, #f87d7d, #f67777, #f47473, #f27070, #f06d6c, #ee6969)",
});

const TextareaSection = styled(Stack)({
    flexDirection: "row",
    height: "125px",
    width: "100%",
    gap: "1rem",
    alignItems: "center",
    justifyContent: "center",

    "@media(width >= 990px)": {
        gap: "3rem",
        width: "30%",
    },

    "@media(width >= 1900px)": {
        width: "35%",
    },
});

const Textarea = styled(Stack)({
    borderRadius: "10px",
    height: "100%",
    width: "100%",
    backgroundColor: "white",

    "& fieldset": { border: "none" },

    "@media(min-width: 768px)": {
        width: "50%",
    },

    "@media(min-width: 990px)": {
        width: "100%",
    },
});
